import React, { FC, useState, useMemo, useEffect, useCallback } from 'react';
import AllColorsPicker from './pickers/allColorsPicker';
import PaletteColorsPicker, { ColorPaletteInterface } from './pickers/paletteColorsPicker';
import styled from 'styled-components';
import { SearchInput } from '../input';
import { Spinner } from '../spinner';

const ColorPickerContainer = styled.div<{ $showScrollbar: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: white;
    z-index: 2;
    padding: 10px;
    border-radius: 5px;
    font-family: sans-serif;
    overflow-x: hidden;
    overflow-y: ${({ $showScrollbar }) => ($showScrollbar ? 'auto' : 'hidden')};
    max-height: ${({ $showScrollbar }) => ($showScrollbar ? '390px' : 'auto')};
`;

const SearchContainer = styled.div`
    position: sticky;
    top: 0;
    width: 100%;
    padding: 10px 10px 16px 10px;
    background-color: white;
    z-index: 10;
    border-radius: 5px 5px 0 0;
`;

const NoResultsMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: #666;
    font-size: 14px;
`;

const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
`;

export interface ColorPickerProps {
    availableColors?: ColorPaletteInterface[] | null;
    color: string | null;
    usedColors?: string[];
    recentColors?: string[];
    onChange: (color: string, name?: string) => void;
    onReset?: () => void;
}

export const ColorPicker: FC<ColorPickerProps> = ({ availableColors, color, usedColors, recentColors, onChange, onReset }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Debounce the search query
    useEffect(() => {
        if (searchQuery !== debouncedSearchQuery) {
            setIsSearching(true);
        }

        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setIsSearching(false);
        }, 700); // 700ms debounce delay

        return () => {
            clearTimeout(timer);
        };
    }, [searchQuery, debouncedSearchQuery]);

    // Filter available colors based on debounced search query
    const filteredColors = useMemo(() => {
        if (!availableColors || !debouncedSearchQuery.trim()) {
            return availableColors;
        }

        return availableColors.filter((colorItem) => colorItem.name?.toLowerCase().includes(debouncedSearchQuery.toLowerCase().trim()));
    }, [availableColors, debouncedSearchQuery]);

    const handleSearch = useCallback((value: string) => {
        setSearchQuery(value);
    }, []);

    // Check if we have search results when searching
    const hasSearchResults = !searchQuery.trim() || (filteredColors && filteredColors.length > 0);

    // Determine if we should show scrollbar based on available colors count
    const shouldShowScrollbar = (availableColors && availableColors.length > 8) || false;

    return (
        <ColorPickerContainer $showScrollbar={shouldShowScrollbar}>
            {/* Show search only when we have available colors */}
            {availableColors && availableColors.length > 10 && (
                <SearchContainer>
                    <SearchInput placeholder='Search color...' onChange={(e) => handleSearch(e.target.value)} value={searchQuery} />
                </SearchContainer>
            )}

            {/* Select all colors picker */}
            {!availableColors && (
                <AllColorsPicker color={color ?? '#000000'} usedColors={usedColors} recentColors={recentColors} onChange={onChange} onReset={onReset} />
            )}

            {/* Show loader when searching with debounce */}
            {availableColors && isSearching && (
                <LoaderContainer>
                    <Spinner />
                </LoaderContainer>
            )}

            {/* Palette colors picker - only show if we have results or no search and not currently searching */}
            {availableColors && hasSearchResults && !isSearching && <PaletteColorsPicker colors={filteredColors || []} onChange={onChange} onReset={onReset} />}

            {/* No results message - only show when not searching */}
            {availableColors && !hasSearchResults && !isSearching && <NoResultsMessage>No colors found matching "{searchQuery}"</NoResultsMessage>}
        </ColorPickerContainer>
    );
};
